import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    AlertTriangle,
    CalendarDays,
    CheckCircle2,
    Clock3,
    CloudRain,
    FileText,
    Flame,
    Loader2,
    Meh,
    Rocket,
    Send,
    Sparkles,
    Target,
    Camera,
    Plus,
    Trash2,
    MapPin,
    Navigation,
    Square,
    Clock,
    Pencil,
    ExternalLink,
} from "lucide-react";

import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import ConfirmModal from "../../components/ui/ConfirmModal";
import ReverseGeocode from "../../components/ReverseGeocode";
import { MIN_EOD_WORDS, getRequiredMinimumWordsError, getMinimumWordsError } from "../../utils/textValidation";
import { getIndiaDateKey, getIndiaDateParts } from "../../utils/indiaTime";

const todayStr = () => getIndiaDateKey();
const isBeforeEodOpenTime = (opensAtMinutes = (18 * 60) + 27, date = new Date()) => {
    const parts = getIndiaDateParts(date);
    return parts && ((parts.hour * 60) + parts.minute < opensAtMinutes);
};
const getEodAccessFallback = (shiftTiming = "") => {
    const matches = [...String(shiftTiming || "").toUpperCase().matchAll(/(0?[1-9]|1[0-2])(?:[:.]([0-5]\d))?\s*(AM|PM)/g)];
    const endTime = matches.at(-1);
    if (!endTime) return { opensAtMinutes: (18 * 60) + 27, opensAt: "6:27 PM IST" };

    let hour = Number(endTime[1]);
    const minute = Number(endTime[2] || 0);
    if (endTime[3] === "PM" && hour < 12) hour += 12;
    if (endTime[3] === "AM" && hour === 12) hour = 0;

    const opensAtMinutes = ((hour * 60) + minute - 3 + 1440) % 1440;
    const opensAtHour = Math.floor(opensAtMinutes / 60);
    const suffix = opensAtHour >= 12 ? "PM" : "AM";
    return {
        opensAtMinutes,
        opensAt: `${opensAtHour % 12 || 12}:${String(opensAtMinutes % 60).padStart(2, "0")} ${suffix} IST`,
    };
};
const EOD_CACHE_TTL_MS = 60 * 1000;

const emptyEodForm = {
    workSummary: "",
    tasksCompleted: 0,
    tasksPending: "",
    blockers: "",
    tomorrowPlan: "",
    dayVibe: "",
    teamMetrics: {},
    salesVisitReport: {
        shopCount: 0,
        details: "",
        photos: [],
        routeTracking: {
            status: "",
            startedAt: "",
            stoppedAt: "",
            totalDistanceMeters: 0,
            points: [],
        },
    },
    manufacturingReport: {
        productionJobs: [],
        downtimeReasons: [],
        qualityDefects: [],
        shiftHandoverNotes: "",
        photos: [],
    },
};

const buildEmptyReport = (date = todayStr()) => ({
    date,
    ...emptyEodForm,
    status: "draft",
});

const getEodCacheKey = (employeeOrId, date) => {
    const id = typeof employeeOrId === "string" ? employeeOrId : employeeOrId?._id || employeeOrId?.id;
    return id && date ? `employee_eod_${id}_${date}` : null;
};

const readCache = (key) => {
    if (!key) return null;

    try {
        const cached = JSON.parse(localStorage.getItem(key) || "null");
        if (!cached?.data || Date.now() - cached.savedAt > EOD_CACHE_TTL_MS) return null;
        return cached.data;
    } catch {
        localStorage.removeItem(key);
        return null;
    }
};

const writeCache = (key, data) => {
    if (!key) return;
    localStorage.setItem(key, JSON.stringify({ data, savedAt: Date.now() }));
};

const hasDraftContent = (draft = {}) => (
    Boolean(
        draft.workSummary
        || draft.tasksPending
        || draft.blockers
        || draft.tomorrowPlan
        || draft.dayVibe
        || Object.values(draft.teamMetrics || {}).some(Boolean)
        || Number(draft.salesVisitReport?.shopCount || 0) > 0
        || draft.salesVisitReport?.details
        || (draft.salesVisitReport?.photos || []).length
        || (draft.salesVisitReport?.routeTracking?.points || []).length
        || (draft.manufacturingReport?.productionJobs || []).length
        || (draft.manufacturingReport?.downtimeReasons || []).length
        || (draft.manufacturingReport?.qualityDefects || []).length
        || draft.manufacturingReport?.shiftHandoverNotes
        || (draft.manufacturingReport?.photos || []).length
    )
);

const isSalesTeam = (team = "") => String(team || "").toLowerCase().includes("sales");
const isManufacturingTeam = (team = "") => String(team || "").toLowerCase().includes("manufactur");
const formatRoleLabel = (role = "") => {
    const value = String(role || "").trim();
    if (!value) return "";
    return value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const calculateDistanceMeters = (from, to) => {
    if (!from?.latitude || !from?.longitude || !to?.latitude || !to?.longitude) return 0;
    const earthRadius = 6371000;
    const lat1 = toNumber(from.latitude) * Math.PI / 180;
    const lat2 = toNumber(to.latitude) * Math.PI / 180;
    const deltaLat = (toNumber(to.latitude) - toNumber(from.latitude)) * Math.PI / 180;
    const deltaLng = (toNumber(to.longitude) - toNumber(from.longitude)) * Math.PI / 180;
    const a = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
    return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const formatDistance = (meters = 0) => {
    const value = Math.max(0, Number(meters || 0));
    if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 1 : 2)} km`;
    return `${Math.round(value)} m`;
};

const calculateRouteDistance = (points = []) =>
    points.reduce((total, point, index) => total + (index ? calculateDistanceMeters(points[index - 1], point) : 0), 0);

const getCurrentLocation = () =>
    new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error("Geolocation is not supported by your browser."));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
            }),
            (error) => reject(new Error(error.code === 1 ? "Please enable location access to capture shop visit location." : "Unable to detect your location. Please try again.")),
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
        );
    });

const isLockedPastSubmission = (report, date) => (
    report?.status === "submitted" && date < todayStr()
);

const vibeMap = {
    crushing_it: {
        icon: <Rocket size={18} />,
        label: "Crushing It",
        summary: "High momentum",
    },
    solid_day: {
        icon: <Target size={18} />,
        label: "Solid Day",
        summary: "Steady progress",
    },
    getting_there: {
        icon: <Meh size={18} />,
        label: "Getting There",
        summary: "Some progress",
    },
    rough_day: {
        icon: <CloudRain size={18} />,
        label: "Rough Day",
        summary: "Needs support",
    },
    on_fire: {
        icon: <Flame size={18} />,
        label: "On Fire",
        summary: "Exceptional output",
    },
};

const EODReport = () => {
    const { employee } = useAuth();
    const employeeId = employee?._id || employee?.id;

    const [selectedDate, setSelectedDate] = useState(todayStr());
    const cachedReport = useMemo(() => readCache(getEodCacheKey(employeeId, selectedDate)), [employeeId, selectedDate]);
    const [report, setReport] = useState(cachedReport || buildEmptyReport(selectedDate));
    const [form, setForm] = useState(() => ({
        ...emptyEodForm,
        ...(cachedReport || {}),
        teamMetrics: cachedReport?.teamMetrics || {},
        manufacturingReport: cachedReport?.manufacturingReport || emptyEodForm.manufacturingReport,
    }));
    const [saving, setSaving] = useState(false);
    const [saveState, setSaveState] = useState(cachedReport?.status === "submitted" ? "submitted" : cachedReport ? "saved" : "");
    const [submitting, setSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submittedPopupOpen, setSubmittedPopupOpen] = useState(false);
    const [earlyAccess, setEarlyAccess] = useState(null);
    const [earlyReasonOpen, setEarlyReasonOpen] = useState(false);
    const [earlyReason, setEarlyReason] = useState('');
    const [earlyReasonSaving, setEarlyReasonSaving] = useState(false);
    const [clockNow, setClockNow] = useState(() => new Date());
    const [salesVisitCameraOpen, setSalesVisitCameraOpen] = useState(false);
    const [salesVisitCameraLoading, setSalesVisitCameraLoading] = useState(false);
    const [salesVisitLocationError, setSalesVisitLocationError] = useState("");
    const [pendingShopPhoto, setPendingShopPhoto] = useState(null); // { data, location, uploadedAt } — waiting for locationName + description
    const [editingShop, setEditingShop] = useState(null); // { index, locationName, description, data, location, uploadedAt }
    const [liveTracking, setLiveTracking] = useState(false);
    const [liveTrackingError, setLiveTrackingError] = useState("");
    const reportRef = useRef(report);
    const formRef = useRef(form);
    const selectedDateRef = useRef(selectedDate);
    const hasLocalEditsRef = useRef(false);
    const autosaveIdRef = useRef(0);
    const loadIdRef = useRef(0);
    const salesVisitVideoRef = useRef(null);
    const salesVisitStreamRef = useRef(null);
    const liveTrackingWatchRef = useRef(null);

    useEffect(() => {
        reportRef.current = report;
    }, [report]);

    useEffect(() => {
        const timer = window.setInterval(() => setClockNow(new Date()), 30000);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        formRef.current = form;
    }, [form]);

    useEffect(() => {
        selectedDateRef.current = selectedDate;
    }, [selectedDate]);

    const applyReport = useCallback((nextReport) => {
        hasLocalEditsRef.current = false;
        setReport(nextReport);
        setForm({
            workSummary: nextReport.workSummary || "",
            tasksCompleted: nextReport.tasksCompleted || 0,
            tasksPending: nextReport.tasksPending || "",
            blockers: nextReport.blockers || "",
            tomorrowPlan: nextReport.tomorrowPlan || "",
            dayVibe: nextReport.dayVibe || "",
            teamMetrics: nextReport.teamMetrics || {},
            salesVisitReport: {
                shopCount: nextReport.salesVisitReport?.shopCount || 0,
                details: nextReport.salesVisitReport?.details || "",
                photos: nextReport.salesVisitReport?.photos || [],
                routeTracking: {
                    status: nextReport.salesVisitReport?.routeTracking?.status || "",
                    startedAt: nextReport.salesVisitReport?.routeTracking?.startedAt || "",
                    stoppedAt: nextReport.salesVisitReport?.routeTracking?.stoppedAt || "",
                    totalDistanceMeters: nextReport.salesVisitReport?.routeTracking?.totalDistanceMeters || 0,
                    points: nextReport.salesVisitReport?.routeTracking?.points || [],
                },
            },
            manufacturingReport: {
                productionJobs: nextReport.manufacturingReport?.productionJobs || [],
                downtimeReasons: nextReport.manufacturingReport?.downtimeReasons || [],
                qualityDefects: nextReport.manufacturingReport?.qualityDefects || [],
                shiftHandoverNotes: nextReport.manufacturingReport?.shiftHandoverNotes || "",
                photos: nextReport.manufacturingReport?.photos || [],
            },
        });
        setLiveTracking(false);
        setSaveState(nextReport.status === "submitted" ? "submitted" : "saved");
    }, []);

    const setFormField = (field, value) => {
        if (isLockedPastSubmission(reportRef.current, selectedDateRef.current)) return;
        hasLocalEditsRef.current = true;
        setSaveState("editing");
        setForm((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const setMetric = (metric, value) => {
        if (isLockedPastSubmission(reportRef.current, selectedDateRef.current)) return;
        hasLocalEditsRef.current = true;
        setSaveState("editing");
        setForm((current) => ({
            ...current,
            teamMetrics: {
                ...(current.teamMetrics || {}),
                [metric]: value,
            },
        }));
    };

    const setSalesVisitField = (field, value) => {
        if (isLockedPastSubmission(reportRef.current, selectedDateRef.current)) return;
        hasLocalEditsRef.current = true;
        setSaveState("editing");
        setForm((current) => ({
            ...current,
            salesVisitReport: {
                ...(current.salesVisitReport || {}),
                [field]: value,
            },
        }));
    };

    const updateManufacturingReport = (updater) => {
        if (isLockedPastSubmission(reportRef.current, selectedDateRef.current)) return;
        hasLocalEditsRef.current = true;
        setSaveState("editing");
        setForm((current) => ({
            ...current,
            manufacturingReport: updater(current.manufacturingReport || emptyEodForm.manufacturingReport),
        }));
    };

    const setManufacturingField = (field, value) => {
        updateManufacturingReport((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const addManufacturingRow = (section, row) => {
        updateManufacturingReport((current) => ({
            ...current,
            [section]: [...(current[section] || []), row],
        }));
    };

    const updateManufacturingRow = (section, index, field, value) => {
        updateManufacturingReport((current) => ({
            ...current,
            [section]: (current[section] || []).map((row, rowIndex) => (
                rowIndex === index ? { ...row, [field]: value } : row
            )),
        }));
    };

    const removeManufacturingRow = (section, index) => {
        updateManufacturingReport((current) => ({
            ...current,
            [section]: (current[section] || []).filter((_, rowIndex) => rowIndex !== index),
        }));
    };

    const readPhotoFile = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({
            name: file.name,
            data: reader.result,
            category: "Issue proof",
            remarks: "",
            uploadedAt: new Date().toISOString(),
        });
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const addManufacturingPhotos = async (files) => {
        const selectedFiles = Array.from(files || []).slice(0, 12);
        if (!selectedFiles.length || isLockedPastSubmission(reportRef.current, selectedDateRef.current)) return;
        const currentPhotos = formRef.current.manufacturingReport?.photos || [];
        const remainingSlots = Math.max(0, 12 - currentPhotos.length);
        if (!remainingSlots) {
            alert("You can attach up to 12 manufacturing proof photos.");
            return;
        }

        const photos = await Promise.all(selectedFiles.slice(0, remainingSlots).map(readPhotoFile));
        updateManufacturingReport((current) => ({
            ...current,
            photos: [...(current.photos || []), ...photos],
        }));
    };

    const updateManufacturingPhoto = (index, field, value) => {
        updateManufacturingReport((current) => ({
            ...current,
            photos: (current.photos || []).map((photo, photoIndex) => (
                photoIndex === index ? { ...photo, [field]: value } : photo
            )),
        }));
    };

    const removeManufacturingPhoto = (index) => {
        updateManufacturingReport((current) => ({
            ...current,
            photos: (current.photos || []).filter((_, photoIndex) => photoIndex !== index),
        }));
    };

    const stopSalesVisitCamera = useCallback(() => {
        if (salesVisitStreamRef.current) {
            salesVisitStreamRef.current.getTracks().forEach((track) => track.stop());
            salesVisitStreamRef.current = null;
        }
        if (salesVisitVideoRef.current) {
            salesVisitVideoRef.current.srcObject = null;
        }
        setSalesVisitCameraOpen(false);
        setSalesVisitCameraLoading(false);
        setSalesVisitLocationError("");
    }, []);

    useEffect(() => () => stopSalesVisitCamera(), [stopSalesVisitCamera]);

    const applyRoutePoint = useCallback((point) => {
        if (isLockedPastSubmission(reportRef.current, selectedDateRef.current)) return;
        hasLocalEditsRef.current = true;
        setSaveState("editing");
        setForm((current) => {
            const currentRoute = current.salesVisitReport?.routeTracking || {};
            const currentPoints = currentRoute.points || [];
            const lastPoint = currentPoints[currentPoints.length - 1];
            if (lastPoint && calculateDistanceMeters(lastPoint, point) < 15) return current;

            const nextPoints = [...currentPoints, point].slice(-600);
            return {
                ...current,
                salesVisitReport: {
                    ...(current.salesVisitReport || {}),
                    routeTracking: {
                        ...currentRoute,
                        status: "active",
                        startedAt: currentRoute.startedAt || point.capturedAt,
                        stoppedAt: "",
                        points: nextPoints,
                        totalDistanceMeters: calculateRouteDistance(nextPoints),
                    },
                },
            };
        });
    }, []);

    const stopLiveTracking = useCallback((markStopped = true) => {
        if (liveTrackingWatchRef.current !== null && navigator.geolocation?.clearWatch) {
            navigator.geolocation.clearWatch(liveTrackingWatchRef.current);
            liveTrackingWatchRef.current = null;
        }
        setLiveTracking(false);
        if (!markStopped || isLockedPastSubmission(reportRef.current, selectedDateRef.current)) return;

        hasLocalEditsRef.current = true;
        setSaveState("editing");
        setForm((current) => {
            const currentRoute = current.salesVisitReport?.routeTracking || {};
            if (!(currentRoute.points || []).length && !currentRoute.startedAt) return current;
            return {
                ...current,
                salesVisitReport: {
                    ...(current.salesVisitReport || {}),
                    routeTracking: {
                        ...currentRoute,
                        status: "stopped",
                        stoppedAt: new Date().toISOString(),
                        totalDistanceMeters: calculateRouteDistance(currentRoute.points || []),
                    },
                },
            };
        });
    }, []);

    useEffect(() => () => stopLiveTracking(false), [stopLiveTracking]);

    const buildStoppedTrackingForm = useCallback((draftForm = formRef.current) => {
        const currentRoute = draftForm.salesVisitReport?.routeTracking || {};
        if (!(currentRoute.points || []).length && !currentRoute.startedAt) return draftForm;
        return {
            ...draftForm,
            salesVisitReport: {
                ...(draftForm.salesVisitReport || {}),
                routeTracking: {
                    ...currentRoute,
                    status: "stopped",
                    stoppedAt: currentRoute.stoppedAt || new Date().toISOString(),
                    totalDistanceMeters: calculateRouteDistance(currentRoute.points || []),
                },
            },
        };
    }, []);

    const startLiveTracking = () => {
        if (isLockedPastSubmission(reportRef.current, selectedDateRef.current)) return;
        if (!navigator.geolocation?.watchPosition) {
            setLiveTrackingError("Live location tracking is not supported on this device/browser.");
            return;
        }

        setLiveTrackingError("");
        setLiveTracking(true);
        liveTrackingWatchRef.current = navigator.geolocation.watchPosition(
            (position) => {
                applyRoutePoint({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    capturedAt: new Date().toISOString(),
                });
            },
            (error) => {
                setLiveTrackingError(error.code === 1 ? "Please allow location permission to use live tracking." : "Live tracking could not get your location.");
                stopLiveTracking(false);
            },
            { enableHighAccuracy: true, timeout: 12000, maximumAge: 10000 },
        );
    };

    const openSalesVisitCamera = async () => {
        if (isLockedPastSubmission(reportRef.current, selectedDateRef.current)) return;
        const existingPhotos = formRef.current.salesVisitReport?.photos || [];
        if (existingPhotos.length >= 12) {
            alert("You can capture up to 12 shop visit photos.");
            return;
        }
        if (!navigator.mediaDevices?.getUserMedia) {
            alert("Camera access is not supported on this device/browser.");
            return;
        }

        try {
            setSalesVisitCameraOpen(true);
            setSalesVisitCameraLoading(true);
            setSalesVisitLocationError("");
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: "environment" } },
                audio: false,
            });
            salesVisitStreamRef.current = stream;
            if (salesVisitVideoRef.current) {
                salesVisitVideoRef.current.srcObject = stream;
                await salesVisitVideoRef.current.play();
            }
        } catch (error) {
            alert(error?.name === "NotAllowedError" ? "Please allow camera access to capture shop visit proof." : "Could not open camera. Please try again.");
            setSalesVisitCameraOpen(false);
        } finally {
            setSalesVisitCameraLoading(false);
        }
    };

    const captureSalesVisitPhoto = async () => {
        const video = salesVisitVideoRef.current;
        if (!video) {
            alert("Camera preview is not ready yet.");
            return;
        }

        const existingPhotos = formRef.current.salesVisitReport?.photos || [];
        if (existingPhotos.length >= 50) {
            alert("You can capture up to 50 shop visit photos.");
            stopSalesVisitCamera();
            return;
        }

        try {
            setSalesVisitCameraLoading(true);
            setSalesVisitLocationError("");
            const location = await getCurrentLocation();
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth || 1280;
            canvas.height = video.videoHeight || 720;
            const context = canvas.getContext("2d");
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Store as pending — user fills location name + description before confirming
            setPendingShopPhoto({
                name: `shop-visit-${Date.now()}.jpg`,
                data: canvas.toDataURL("image/jpeg", 0.85),
                uploadedAt: new Date().toISOString(),
                source: "camera",
                location,
                locationName: "",
                description: "",
            });
            stopSalesVisitCamera();
        } catch (error) {
            setSalesVisitLocationError(error.message || "Location not detected. Please enable GPS/location permission and tap Retry location.");
        } finally {
            setSalesVisitCameraLoading(false);
        }
    };

    const confirmShopPhoto = () => {
        if (!pendingShopPhoto) return;
        const existingPhotos = formRef.current.salesVisitReport?.photos || [];
        const nextPhotos = [...existingPhotos, pendingShopPhoto];
        const nextForm = {
            ...formRef.current,
            salesVisitReport: {
                ...(formRef.current.salesVisitReport || {}),
                shopCount: nextPhotos.length,
                photos: nextPhotos,
            },
        };
        formRef.current = nextForm;
        setForm(nextForm);
        hasLocalEditsRef.current = true;
        setSaveState("editing");
        setPendingShopPhoto(null);
        void saveDraft(nextForm, selectedDateRef.current, { immediate: true });
    };

    const removeSalesVisitPhoto = (index) => {
        if (isReadOnlyPastSubmission) return;
        const photos = formRef.current.salesVisitReport?.photos || [];
        const nextPhotos = photos.filter((_, itemIndex) => itemIndex !== index);
        const nextForm = {
            ...formRef.current,
            salesVisitReport: {
                ...(formRef.current.salesVisitReport || {}),
                shopCount: nextPhotos.length,
                photos: nextPhotos,
            },
        };
        formRef.current = nextForm;
        setForm(nextForm);
        hasLocalEditsRef.current = true;
        setSaveState("editing");
        void saveDraft(nextForm, selectedDateRef.current, { immediate: true });
    };

    const openEditShop = (index) => {
        if (isReadOnlyPastSubmission) return;
        const photos = formRef.current.salesVisitReport?.photos || [];
        if (photos[index]) {
            setEditingShop({
                index,
                ...photos[index],
            });
        }
    };

    const saveEditedShop = () => {
        if (!editingShop) return;
        const photos = formRef.current.salesVisitReport?.photos || [];
        const nextPhotos = photos.map((p, i) => i === editingShop.index ? {
            ...p,
            locationName: editingShop.locationName || "",
            description: editingShop.description || "",
        } : p);
        const nextForm = {
            ...formRef.current,
            salesVisitReport: {
                ...(formRef.current.salesVisitReport || {}),
                shopCount: nextPhotos.length,
                photos: nextPhotos,
            },
        };
        formRef.current = nextForm;
        setForm(nextForm);
        hasLocalEditsRef.current = true;
        setSaveState("editing");
        setEditingShop(null);
        void saveDraft(nextForm, selectedDateRef.current, { immediate: true });
    };

    const fetchDraft = useCallback(async (date, loadId) => {
        const res = await api.get("/api/employee/eod/draft", {
            params: { date },
        });

        const nextReport = res.data;
        if (loadId !== loadIdRef.current || hasLocalEditsRef.current) return;
        setEarlyAccess(nextReport.earlyAccess || null);

        applyReport(nextReport);
        writeCache(getEodCacheKey(employeeId, date), nextReport);
    }, [applyReport, employeeId]);

    const saveDraft = useCallback(async (draftForm = formRef.current, date = selectedDateRef.current, { immediate = false } = {}) => {
        const currentReport = reportRef.current;
        if (!currentReport || currentReport.status === "submitted" || isLockedPastSubmission(currentReport, date)) return;
        if (!immediate && !hasDraftContent(draftForm)) return;

        const optimisticDraft = {
            ...currentReport,
            date,
            ...draftForm,
            status: "draft",
            updatedAt: new Date().toISOString(),
        };

        setReport(optimisticDraft);
        writeCache(getEodCacheKey(employeeId, date), optimisticDraft);
        setSaving(true);
        setSaveState("saving");

        const autosaveId = autosaveIdRef.current + 1;
        autosaveIdRef.current = autosaveId;

        try {
            const res = await api.patch(
                "/api/employee/eod/draft",
                {
                    date,
                    ...draftForm,
                },
                { skipSaveToast: true },
            );

            if (autosaveId !== autosaveIdRef.current) return;

            setReport(res.data);
            writeCache(getEodCacheKey(employeeId, date), res.data);
            setSaveState("saved");
            hasLocalEditsRef.current = false;
        } catch {
            setSaveState("editing");
        } finally {
            if (immediate || autosaveId === autosaveIdRef.current) {
                setSaving(false);
            }
        }
    }, [employeeId]);

    useEffect(() => {
        let active = true;

        const load = async () => {
            const loadId = loadIdRef.current + 1;
            loadIdRef.current = loadId;

            const cachedForDate = readCache(getEodCacheKey(employeeId, selectedDate));
            if (!hasLocalEditsRef.current) {
                if (cachedForDate) {
                    applyReport(cachedForDate);
                } else {
                    applyReport(buildEmptyReport(selectedDate));
                }
            }

            try {
                await fetchDraft(selectedDate, loadId);
            } catch {
                // Keep the immediately rendered local draft visible.
            }
        };

        if (active) load();

        return () => {
            active = false;
        };
    }, [applyReport, employeeId, fetchDraft, selectedDate]);

    const handleDateChange = async (event) => {
        const nextDate = event.target.value;
        const draftToSave = liveTracking ? buildStoppedTrackingForm(formRef.current) : formRef.current;
        if (liveTracking) {
            formRef.current = draftToSave;
            setForm(draftToSave);
        }
        stopLiveTracking(false);
        await saveDraft(draftToSave, selectedDateRef.current, { immediate: true });
        hasLocalEditsRef.current = false;
        setSelectedDate(nextDate);
    };

    useEffect(() => {
        if (!hasLocalEditsRef.current || !hasDraftContent(form)) return undefined;

        const timeout = setTimeout(async () => {
            await saveDraft(form, selectedDate);
        }, 700);

        return () => clearTimeout(timeout);
    }, [form, saveDraft, selectedDate]);

    useEffect(() => {
        const flushBeforeUnload = () => {
            const draftForm = formRef.current;
            const date = selectedDateRef.current;
            const currentReport = reportRef.current;

            if (!hasLocalEditsRef.current || !currentReport || currentReport.status === "submitted" || !hasDraftContent(draftForm)) return;

            const optimisticDraft = {
                ...currentReport,
                date,
                ...draftForm,
                status: "draft",
                updatedAt: new Date().toISOString(),
            };

            writeCache(getEodCacheKey(employeeId, date), optimisticDraft);
        };

        window.addEventListener("beforeunload", flushBeforeUnload);

        return () => {
            flushBeforeUnload();
            window.removeEventListener("beforeunload", flushBeforeUnload);
        };
    }, [employeeId]);

    const saveNow = () => {
        if (isLockedPastSubmission(reportRef.current, selectedDateRef.current)) return;
        void saveDraft(formRef.current, selectedDateRef.current, { immediate: true });
    };

    const submitEOD = async () => {
        if (isLockedPastSubmission(reportRef.current, selectedDateRef.current)) {
            alert("Submitted EOD reports from previous days are view-only.");
            setConfirmOpen(false);
            return;
        }
        const submissionForm = liveTracking ? buildStoppedTrackingForm(formRef.current) : form;
        if (liveTracking) {
            formRef.current = submissionForm;
            setForm(submissionForm);
        }
        stopLiveTracking(false);

        const validationError = isSalesTeam(employee?.team)
            ? getRequiredMinimumWordsError(submissionForm.tomorrowPlan, "Tomorrow plan")
            : (getRequiredMinimumWordsError(submissionForm.workSummary, "Work summary")
               || getRequiredMinimumWordsError(submissionForm.tomorrowPlan, "Tomorrow plan"));

        if (validationError) {
            alert(validationError);
            return;
        }

        const previousReport = report;
        const optimisticSubmitted = {
            ...(report || {}),
            date: selectedDate,
            ...submissionForm,
            status: "submitted",
            submittedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };

        setSubmitting(true);
        setConfirmOpen(false);
        setSaveState("submitted");
        setSubmittedPopupOpen(true);
        setReport(optimisticSubmitted);
        writeCache(getEodCacheKey(employeeId, selectedDate), optimisticSubmitted);

        try {
            const res = await api.post(
                "/api/employee/eod/submit",
                {
                    date: selectedDate,
                    ...submissionForm,
                },
                { skipSaveToast: true },
            );

            setReport(res.data);
            writeCache(getEodCacheKey(employeeId, selectedDate), res.data);
            setSaveState("submitted");
            setSubmittedPopupOpen(true);
        } catch (err) {
            setReport(previousReport);
            writeCache(getEodCacheKey(employeeId, selectedDate), previousReport);
            setSaveState(previousReport?.status === "submitted" ? "submitted" : "saved");
            setSubmittedPopupOpen(false);
            alert(err.response?.data?.message || "Submit failed");
        } finally {
            setSubmitting(false);
        }
    };

    const submitEarlyReason = async (event) => {
        event.preventDefault();
        const reasonError = getMinimumWordsError(earlyReason, "Early EOD reason", 5);
        if (reasonError) {
            alert(reasonError);
            return;
        }
        try {
            setEarlyReasonSaving(true);
            const res = await api.post("/api/employee/eod/early-access", { date: selectedDate, reason: earlyReason.trim() });
            setEarlyAccess(res.data);
            setEarlyReasonOpen(false);
            setEarlyReason("");
        } catch (error) {
            alert(error.response?.data?.message || "Unable to submit the reason.");
        } finally {
            setEarlyReasonSaving(false);
        }
    };
    const isSalesEmployee = isSalesTeam(employee?.team);
    const isManufacturingEmployee = isManufacturingTeam(employee?.team);

    const completion = useMemo(() => {
        const fields = isSalesEmployee
            ? [form.tomorrowPlan]
            : [form.workSummary, form.tomorrowPlan];
        if (false && isManufacturingEmployee) {
            fields.push(
                form.teamMetrics?.["Planned Production"],
                form.teamMetrics?.["Actual Production"],
                form.teamMetrics?.["Rejected Units"],
                form.teamMetrics?.["Downtime"],
                form.teamMetrics?.["Safety / 5S Status"],
                (form.manufacturingReport?.productionJobs || []).length ? "production-jobs" : "",
                (form.manufacturingReport?.downtimeReasons || []).length ? "downtime-reasons" : "",
                (form.manufacturingReport?.qualityDefects || []).length ? "quality-defects" : "",
                form.manufacturingReport?.shiftHandoverNotes,
            );
        }
        const filled = fields.filter(Boolean).length;
        return Math.round((filled / fields.length) * 100);
    }, [form, isSalesEmployee, isManufacturingEmployee]);

    const metrics = getTeamMetrics(employee?.team);
    const fallbackEodAccess = useMemo(() => getEodAccessFallback(employee?.shiftTiming), [employee?.shiftTiming]);
    const eodAccess = earlyAccess || fallbackEodAccess;
    const reportStatus = report?.status || "draft";
    const isSubmitted = reportStatus === "submitted";
    const isReadOnlyPastSubmission = isLockedPastSubmission(report, selectedDate);
    const isEarlyGateLocked = selectedDate === todayStr()
        && isBeforeEodOpenTime(eodAccess.opensAtMinutes, clockNow)
        && !earlyAccess?.granted;
    const indicatorState = isSubmitted || saveState === "submitted" ? "submitted" : saveState;
    const indicatorLabel =
        indicatorState === "submitted" ? "Submitted"
        : indicatorState === "saving" ? "Saving..."
        : indicatorState === "saved" ? "Saved"
        : indicatorState === "editing" ? "Unsaved changes"
        : "";
    const selectedVibe = vibeMap[form.dayVibe];
    const salesPhotos = form.salesVisitReport?.photos || [];
    const salesRoute = form.salesVisitReport?.routeTracking || {};
    const salesRoutePoints = salesRoute.points || [];
    const salesRouteDistance = salesRoute.totalDistanceMeters || calculateRouteDistance(salesRoutePoints);
    const salesPhotoDistances = salesPhotos.map((photo, index) => ({
        fromFirst: index > 0 ? calculateDistanceMeters(salesPhotos[0]?.location, photo.location) : 0,
        fromPrevious: index > 0 ? calculateDistanceMeters(salesPhotos[index - 1]?.location, photo.location) : 0,
    }));
    const manufacturingReport = form.manufacturingReport || emptyEodForm.manufacturingReport;
    const manufacturingPhotos = manufacturingReport.photos || [];

    return (
            <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
                <main className="eod-premium">
                    <section className="eod-shell">
                        <div className="eod-hero">
                            <div>
                                <span className="premium-eyebrow">End of Day</span>
                                <h1>Daily report workspace</h1>
                                <div className="premium-meta-row">
                                    <span>{employee?.team || "Team"}</span>
                                    <span>{formatRoleLabel(employee?.role) || "Employee"}</span>
                                    <span>{selectedDate}</span>
                                </div>
                            </div>

                            <div className="eod-hero-metrics">
                                <EodMetric label="Completion" value={`${completion}%`} />
                                <EodMetric label="Status" value={isSubmitted ? "Done" : "Draft"} />
                                <EodMetric label="Vibe" value={selectedVibe?.label || "-"} />
                            </div>
                        </div>

                        {isEarlyGateLocked && (
                            <section className="rounded-2xl border border-amber-400/40 bg-amber-50 p-5 text-amber-950 shadow-sm dark:bg-amber-950/20 dark:text-amber-100">
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        <span className="premium-section-kicker">Available after {eodAccess.opensAt}</span>
                                        <h2 className="mt-1 text-xl font-bold">EOD report is locked until the workday closes</h2>
                                        <p className="mt-1 text-sm opacity-80">Need to fill it earlier? Submit a valid reason to unlock today's report.</p>
                                    </div>
                                    <button type="button" className="premium-primary-button" onClick={() => setEarlyReasonOpen(true)}>
                                        Provide early reason
                                    </button>
                                </div>
                            </section>
                        )}

                        <div
                            className={`eod-grid ${isEarlyGateLocked ? "pointer-events-none select-none opacity-50" : ""}`}
                            style={{ gridTemplateColumns: "minmax(0, 1fr)" }}
                            inert={isEarlyGateLocked ? true : undefined}
                        >
                            <section className="eod-card eod-editor-card">
                                <div className="eod-toolbar">
                                    <div className="eod-date-control">
                                        <CalendarDays size={17} />
                                        <input type="date" max={todayStr()} value={selectedDate} onChange={handleDateChange} />
                                    </div>

                                    <div className="eod-toolbar-actions">
                                        {isSalesEmployee ? (
                                            !isReadOnlyPastSubmission && (
                                                <button
                                                    type="button"
                                                    onClick={openSalesVisitCamera}
                                                    className="premium-primary-button inline-flex items-center gap-2 shadow-md"
                                                >
                                                    <Camera size={16} />
                                                    <span>Add Shop Visit</span>
                                                </button>
                                            )
                                        ) : (
                                            <>
                                                <span className={`eod-status-pill ${reportStatus}`}>
                                                    {isSubmitted ? <CheckCircle2 size={15} /> : <Clock3 size={15} />}
                                                    {isSubmitted ? "Submitted" : "Draft"}
                                                </span>
                                                {saving && (
                                                    <span className="eod-saving">
                                                        <Loader2 size={15} />
                                                        Saving
                                                    </span>
                                                )}
                                                {!saving && indicatorLabel && (
                                                    <span className={`eod-saving ${indicatorState}`}>
                                                        {indicatorState === "submitted" ? <CheckCircle2 size={15} /> : <Clock3 size={15} />}
                                                        {indicatorLabel}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>


                                <div className="eod-field-grid">
                                    {!isSalesEmployee && (
                                    <ClassicReportField icon={<Sparkles size={18} />} label="Today's work summary" helper="What did you complete or work on today?">
                                        <textarea
                                            value={form.workSummary}
                                            onChange={(event) => setFormField("workSummary", event.target.value)}
                                            onBlur={saveNow}
                                            readOnly={isReadOnlyPastSubmission}
                                            placeholder={`At least ${MIN_EOD_WORDS} words...`}
                                        />
                                    </ClassicReportField>
                                    )}

                                    {false && <ClassicReportField icon={<Target size={18} />} label="Pending tasks" helper="What still needs attention?">
                                        <textarea
                                            value={form.tasksPending}
                                            onChange={(event) => setFormField("tasksPending", event.target.value)}
                                            onBlur={saveNow}
                                            readOnly={isReadOnlyPastSubmission}
                                            placeholder={`At least ${MIN_EOD_WORDS} words...`}
                                        />
                                    </ClassicReportField>}

                                    {false && <ClassicReportField icon={<AlertTriangle size={18} />} label="Blockers" helper="Anything slowing progress?">
                                        <textarea
                                            value={form.blockers}
                                            onChange={(event) => setFormField("blockers", event.target.value)}
                                            onBlur={saveNow}
                                            readOnly={isReadOnlyPastSubmission}
                                            placeholder={`At least ${MIN_EOD_WORDS} words...`}
                                        />
                                    </ClassicReportField>}

                                    {!isSalesEmployee && (
                                    <ClassicReportField icon={<Rocket size={18} />} label="Tomorrow's plan" helper="What are you planning to work on tomorrow?">
                                        <textarea
                                            value={form.tomorrowPlan}
                                            onChange={(event) => setFormField("tomorrowPlan", event.target.value)}
                                            onBlur={saveNow}
                                            readOnly={isReadOnlyPastSubmission}
                                            placeholder={`At least ${MIN_EOD_WORDS} words...`}
                                        />
                                    </ClassicReportField>
                                    )}
                                </div>

                                {metrics.length > 0 && (
                                    <section className="hidden eod-team-metrics">
                                        <div className="eod-section-heading">
                                            <div>
                                                <span className="premium-section-kicker">Team metrics</span>
                                                <h2>{getMetricTitle(employee?.team)}</h2>
                                            </div>
                                            <Target size={18} />
                                        </div>

                                        <div className="eod-metric-grid">
                                            {metrics.map((metric) => (
                                                <label key={metric.name} className="eod-metric-input">
                                                    <span>{metric.name}</span>
                                                    <input
                                                        type={metric.type}
                                                        value={form.teamMetrics?.[metric.name] || ""}
                                                        onChange={(event) => setMetric(metric.name, event.target.value)}
                                                        onBlur={saveNow}
                                                        disabled={isReadOnlyPastSubmission}
                                                        placeholder="Enter value"
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {isManufacturingEmployee && (
                                    <section className="eod-sales-section">
                                        <div className="eod-section-heading">
                                            <div>
                                                <span className="premium-section-kicker">Manufacturing Control</span>
                                                <h2>Production, quality and handover</h2>
                                            </div>
                                            <FileText size={18} />
                                        </div>

                                        <ManufacturingRows
                                            title="Job / order-wise production"
                                            actionLabel="Add job"
                                            rows={manufacturingReport.productionJobs || []}
                                            emptyRow={{ orderNo: "", product: "", plannedQty: "", actualQty: "", rejectedQty: "", status: "" }}
                                            fields={[
                                                { key: "orderNo", label: "Job / Order", placeholder: "WO-1024" },
                                                { key: "product", label: "Product", placeholder: "Product / part" },
                                                { key: "plannedQty", label: "Planned", type: "number" },
                                                { key: "actualQty", label: "Actual", type: "number" },
                                                { key: "rejectedQty", label: "Rejected", type: "number" },
                                                { key: "status", label: "Status", placeholder: "Done / Hold" },
                                            ]}
                                            onAdd={(row) => addManufacturingRow("productionJobs", row)}
                                            onChange={(index, field, value) => updateManufacturingRow("productionJobs", index, field, value)}
                                            onRemove={(index) => removeManufacturingRow("productionJobs", index)}
                                            onBlur={saveNow}
                                            readOnly={isReadOnlyPastSubmission}
                                        />

                                        <ManufacturingRows
                                            title="Downtime reason codes"
                                            actionLabel="Add downtime"
                                            rows={manufacturingReport.downtimeReasons || []}
                                            emptyRow={{ reason: "", machine: "", durationMinutes: "", remarks: "" }}
                                            fields={[
                                                { key: "reason", label: "Reason", placeholder: "Breakdown / material delay" },
                                                { key: "machine", label: "Machine / Line", placeholder: "M-04" },
                                                { key: "durationMinutes", label: "Minutes", type: "number" },
                                                { key: "remarks", label: "Remarks", placeholder: "Action taken" },
                                            ]}
                                            onAdd={(row) => addManufacturingRow("downtimeReasons", row)}
                                            onChange={(index, field, value) => updateManufacturingRow("downtimeReasons", index, field, value)}
                                            onRemove={(index) => removeManufacturingRow("downtimeReasons", index)}
                                            onBlur={saveNow}
                                            readOnly={isReadOnlyPastSubmission}
                                        />

                                        <ManufacturingRows
                                            title="Quality defect breakdown"
                                            actionLabel="Add defect"
                                            rows={manufacturingReport.qualityDefects || []}
                                            emptyRow={{ defectType: "", quantity: "", rootCause: "", correctiveAction: "" }}
                                            fields={[
                                                { key: "defectType", label: "Defect", placeholder: "Scratch / dimension" },
                                                { key: "quantity", label: "Qty", type: "number" },
                                                { key: "rootCause", label: "Root cause", placeholder: "Cause if known" },
                                                { key: "correctiveAction", label: "Corrective action", placeholder: "Action taken" },
                                            ]}
                                            onAdd={(row) => addManufacturingRow("qualityDefects", row)}
                                            onChange={(index, field, value) => updateManufacturingRow("qualityDefects", index, field, value)}
                                            onRemove={(index) => removeManufacturingRow("qualityDefects", index)}
                                            onBlur={saveNow}
                                            readOnly={isReadOnlyPastSubmission}
                                        />

                                        <ClassicReportField
                                            icon={<Clock3 size={18} />}
                                            label="Shift handover notes"
                                            helper="Machine condition, pending jobs, QC holds, material shortage, and next-shift priorities."
                                        >
                                            <textarea
                                                value={manufacturingReport.shiftHandoverNotes || ""}
                                                onChange={(event) => setManufacturingField("shiftHandoverNotes", event.target.value)}
                                                onBlur={saveNow}
                                                readOnly={isReadOnlyPastSubmission}
                                                placeholder="Write clear handover notes for the next shift..."
                                            />
                                        </ClassicReportField>

                                        <div className="eod-sales-action-panel">
                                            <div>
                                                <span className="premium-section-kicker">Issue proof</span>
                                                <strong>{manufacturingPhotos.length ? `${manufacturingPhotos.length} photo${manufacturingPhotos.length === 1 ? "" : "s"} attached` : "Attach proof photos"}</strong>
                                                <small>Defects, machine issues, shortages, finished batch</small>
                                            </div>
                                            {!isReadOnlyPastSubmission && (
                                                <label className="eod-add-photo-button">
                                                    <Plus size={16} />
                                                    Add photos
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        multiple
                                                        className="hidden"
                                                        onChange={(event) => {
                                                            addManufacturingPhotos(event.target.files);
                                                            event.target.value = "";
                                                        }}
                                                    />
                                                </label>
                                            )}
                                        </div>

                                        {manufacturingPhotos.length > 0 ? (
                                            <div className="eod-sales-photo-grid">
                                                {manufacturingPhotos.map((photo, index) => (
                                                    <div key={`${photo.name}-${index}`} className="eod-sales-photo">
                                                        <img src={photo.data} alt={photo.name || `Manufacturing proof ${index + 1}`} />
                                                        <div>
                                                            <span>{photo.category || `Proof ${index + 1}`}</span>
                                                            {!isReadOnlyPastSubmission && (
                                                                <button type="button" onClick={() => removeManufacturingPhoto(index)} title="Remove photo">
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                        {!isReadOnlyPastSubmission ? (
                                                            <>
                                                                <input
                                                                    className="mt-2 w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1 text-xs"
                                                                    value={photo.category || ""}
                                                                    onChange={(event) => updateManufacturingPhoto(index, "category", event.target.value)}
                                                                    onBlur={saveNow}
                                                                    placeholder="Category"
                                                                />
                                                                <input
                                                                    className="mt-2 w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1 text-xs"
                                                                    value={photo.remarks || ""}
                                                                    onChange={(event) => updateManufacturingPhoto(index, "remarks", event.target.value)}
                                                                    onBlur={saveNow}
                                                                    placeholder="Remarks"
                                                                />
                                                            </>
                                                        ) : photo.remarks ? (
                                                            <small>{photo.remarks}</small>
                                                        ) : null}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="eod-sales-empty-photos">
                                                <Camera size={18} />
                                                <span>No manufacturing proof photos attached yet.</span>
                                            </div>
                                        )}
                                    </section>
                                )}

                                {isSalesEmployee && (
                                    <section className="eod-sales-section relative">
                                        <div className="flex items-center justify-between gap-3 mb-6 pb-4 border-b border-[var(--border-color)]">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="premium-section-kicker">Field Operations</span>
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500/15 to-violet-500/15 px-2.5 py-0.5 text-[10px] font-black text-[var(--accent-primary)] border border-[var(--accent-primary)]/20">
                                                        {salesPhotos.length} {salesPhotos.length === 1 ? "Shop" : "Shops"} Visited
                                                    </span>
                                                </div>
                                                <h2 className="text-xl font-bold text-[var(--text-primary)] mt-1">Shop Visits Log</h2>
                                            </div>
                                        </div>

                                        {salesPhotos.length === 0 && (
                                            <div className="eod-sales-empty-photos rounded-2xl border-2 border-dashed border-[var(--border-color)] p-8 text-center bg-[var(--bg-secondary)]/50">
                                                <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/20 to-violet-500/20 flex items-center justify-center mb-3">
                                                    <Camera size={26} className="text-[var(--accent-primary)]" />
                                                </div>
                                                <h3 className="font-bold text-base text-[var(--text-primary)]">No shop visits logged today</h3>
                                                <p className="text-xs text-[var(--text-secondary)] mt-1 max-w-sm mx-auto">Tap the luxurious "Add Shop Visit" button in the top right to capture proof with auto-detected GPS & time.</p>
                                            </div>
                                        )}

                                        <div className="space-y-4">
                                            {salesPhotos.map((photo, index) => (
                                                <div key={`shop-${index}`} className="group relative rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm hover:shadow-md transition-all overflow-hidden">
                                                    {/* Header */}
                                                    <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]/60">
                                                        <div className="flex items-center gap-2.5">
                                                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-[var(--accent-primary)] text-white text-xs font-black shadow-sm">
                                                                {index + 1}
                                                            </span>
                                                            <span className="text-xs font-bold text-[var(--text-primary)]">
                                                                {photo.locationName || `Shop #${index + 1}`}
                                                            </span>
                                                        </div>

                                                        <div className="flex items-center gap-2">
                                                            {/* Auto-detected timing pill */}
                                                            {(photo.uploadedAt || photo.capturedAt) && (
                                                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] text-[11px] font-bold text-[var(--text-secondary)] shadow-sm" title="Auto-detected Capture Time">
                                                                    <Clock size={12} className="text-amber-500" />
                                                                    <span>
                                                                        {new Date(photo.uploadedAt || photo.capturedAt).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {/* Edit and Delete Actions */}
                                                            {!isReadOnlyPastSubmission && (
                                                                <div className="flex items-center gap-1">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => openEditShop(index)}
                                                                        className="p-1.5 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] hover:bg-[var(--accent-primary)]/10 hover:text-[var(--accent-primary)] text-[var(--text-secondary)] transition-all shadow-sm"
                                                                        title="Edit Shop Details"
                                                                    >
                                                                        <Pencil size={13} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            if (window.confirm(`Delete Shop #${index + 1} visit?`)) {
                                                                                removeSalesVisitPhoto(index);
                                                                            }
                                                                        }}
                                                                        className="p-1.5 rounded-lg border border-red-500/20 bg-[var(--bg-primary)] hover:bg-red-500/10 text-red-500 transition-all shadow-sm"
                                                                        title="Delete Shop Visit"
                                                                    >
                                                                        <Trash2 size={13} />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Card Body */}
                                                    <div className="p-4 flex flex-col sm:flex-row gap-4">
                                                        <div className="relative group/img flex-shrink-0 mx-auto sm:mx-0">
                                                            <img
                                                                src={photo.data}
                                                                alt={`Shop ${index + 1}`}
                                                                className="h-28 w-28 rounded-xl object-cover border border-[var(--border-color)] shadow-sm cursor-pointer hover:opacity-95 transition-opacity"
                                                                onClick={() => window.open(photo.data, "_blank")}
                                                            />
                                                        </div>

                                                        <div className="flex-1 flex flex-col gap-2 min-w-0 justify-center">
                                                            {/* Location Auto-Detected, Address & Time */}
                                                            <div className="flex items-start gap-2 text-xs text-[var(--text-secondary)] bg-[var(--bg-secondary)] px-3.5 py-2.5 rounded-xl border border-[var(--border-color)]">
                                                                <MapPin size={14} className="flex-shrink-0 mt-0.5 text-rose-500" />
                                                                <div className="flex-1 space-y-1">
                                                                    {/* Row 1: Location Name */}
                                                                    <div className="font-bold text-sm text-[var(--text-primary)] leading-tight">
                                                                        {photo.locationName || `Shop #${index + 1}`}
                                                                    </div>
                                                                    {/* Row 2: Reverse Geocoded Address */}
                                                                    <div className="text-xs text-[var(--text-secondary)] leading-snug">
                                                                        <ReverseGeocode latitude={photo.location?.latitude} longitude={photo.location?.longitude} showGps={false} />
                                                                    </div>
                                                                    {/* Row 3: Auto-detected Timing */}
                                                                    <div className="flex items-center gap-3 pt-0.5 text-[11px] font-medium text-[var(--text-tertiary)]">
                                                                        {(photo.uploadedAt || photo.capturedAt) && (
                                                                            <span className="flex items-center gap-1 font-bold text-amber-600 dark:text-amber-400">
                                                                                <Clock size={12} className="text-amber-500" />
                                                                                Time: {new Date(photo.uploadedAt || photo.capturedAt).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                                                            </span>
                                                                        )}
                                                                        {photo.location?.latitude && (
                                                                            <a
                                                                                href={`https://www.google.com/maps?q=${photo.location.latitude},${photo.location.longitude}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="font-mono opacity-80 hover:opacity-100 hover:text-rose-500 hover:underline transition-all inline-flex items-center gap-1 cursor-pointer"
                                                                                title="Open in Google Maps"
                                                                            >
                                                                                <span>GPS: {Number(photo.location.latitude).toFixed(5)}, {Number(photo.location.longitude).toFixed(5)}</span>
                                                                                <ExternalLink size={10} className="text-rose-500" />
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Description */}
                                                            {photo.description ? (
                                                                <div className="p-2.5 rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] text-xs text-[var(--text-primary)] leading-relaxed whitespace-pre-wrap">
                                                                    {photo.description}
                                                                </div>
                                                            ) : (
                                                                <div className="text-xs text-[var(--text-secondary)] italic">
                                                                    No description added. Click the pencil icon to add notes.
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                <section className="hidden eod-vibe-section">
                                    <div className="eod-section-heading">
                                        <div>
                                            <span className="premium-section-kicker">Day vibe</span>
                                            <h2>How did the day land?</h2>
                                        </div>
                                        <Flame size={18} />
                                    </div>

                                    <div className="eod-vibe-grid">
                                        {Object.entries(vibeMap).map(([key, vibe]) => (
                                            <button
                                                key={key}
                                                className={`eod-vibe-button ${form.dayVibe === key ? "active" : ""}`}
                                                onClick={() => setFormField("dayVibe", key)}
                                                disabled={isReadOnlyPastSubmission}
                                            >
                                                {vibe.icon}
                                                <span>{vibe.label}</span>
                                                <small>{vibe.summary}</small>
                                            </button>
                                        ))}
                                    </div>
                                </section>

                                <div className="eod-submit-bar">
                                    <div>
                                        <strong>{completion}% complete</strong>
                                        <span>{isReadOnlyPastSubmission ? "Submitted previous-day reports are view-only." : "Autosaves while you write."}</span>
                                    </div>
                                    {isReadOnlyPastSubmission ? (
                                        <span className="eod-readonly-pill">
                                            <CheckCircle2 size={16} />
                                            View only
                                        </span>
                                    ) : (
                                        <button className="premium-primary-button" onClick={() => setConfirmOpen(true)} disabled={submitting}>
                                            <Send size={17} />
                                            {submitting ? "Submitting..." : "Submit Report"}
                                        </button>
                                    )}
                                </div>
                            </section>

                        </div>
                    </section>
                </main>

                {/* Pending shop photo — fill location name + description before saving */}
                {pendingShopPhoto && (
                    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                        <section className="w-full max-w-lg bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl shadow-2xl p-5 space-y-4">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-widest text-[var(--accent-primary)]">Shop {salesPhotos.length + 1}</span>
                                    <h2 className="text-xl font-bold">Add Shop Details</h2>
                                    <p className="text-sm text-[var(--text-secondary)] mt-0.5">Photo captured. Review auto-detected details and add notes.</p>
                                </div>
                                <button type="button" onClick={() => setPendingShopPhoto(null)} className="w-9 h-9 rounded-xl border border-[var(--border-color)] flex items-center justify-center">
                                    <span className="text-lg leading-none">&times;</span>
                                </button>
                            </div>

                            {/* Preview */}
                            <img src={pendingShopPhoto.data} alt="Captured" className="h-44 w-full rounded-2xl object-cover border border-[var(--border-color)] shadow-inner" />

                            {/* Auto-detected Timing & GPS */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div className="flex items-center gap-2 text-xs font-semibold text-[var(--text-primary)] bg-[var(--bg-secondary)] px-3 py-2 rounded-xl border border-[var(--border-color)]">
                                    <Clock size={14} className="text-amber-500 flex-shrink-0" />
                                    <div className="truncate">
                                        <span className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] block font-bold">Auto-detected Time</span>
                                        <span>
                                            {new Date(pendingShopPhoto.uploadedAt || pendingShopPhoto.capturedAt).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 text-xs font-semibold text-[var(--text-primary)] bg-[var(--bg-secondary)] px-3 py-2 rounded-xl border border-[var(--border-color)]">
                                    <MapPin size={14} className="text-rose-500 flex-shrink-0" />
                                    <div className="truncate flex-1">
                                        <span className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] block font-bold">Auto-detected GPS</span>
                                        <span className="truncate block">
                                            {pendingShopPhoto.location?.latitude ? (
                                                <ReverseGeocode latitude={pendingShopPhoto.location.latitude} longitude={pendingShopPhoto.location.longitude} />
                                            ) : "Location not detected"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Location name */}
                            <div>
                                <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">
                                    Shop / Client Location Name
                                </label>
                                <input
                                    type="text"
                                    autoFocus
                                    placeholder="e.g. ABC Electronics, MG Road Branch"
                                    value={pendingShopPhoto.locationName || ""}
                                    onChange={(e) => setPendingShopPhoto((p) => ({ ...p, locationName: e.target.value }))}
                                    className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3.5 py-2.5 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] transition-all"
                                />
                            </div>

                            {/* Description — no word limit */}
                            <div>
                                <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">
                                    Shop Visit Notes & Discussion (No word limit)
                                </label>
                                <textarea
                                    rows={3}
                                    placeholder="Enter what was discussed, outcome, follow-up..."
                                    value={pendingShopPhoto.description || ""}
                                    onChange={(e) => setPendingShopPhoto((p) => ({ ...p, description: e.target.value }))}
                                    className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3.5 py-2.5 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] transition-all resize-none"
                                />
                            </div>

                            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end pt-2">
                                <button type="button" onClick={() => setPendingShopPhoto(null)} className="px-4 py-2 rounded-xl border border-[var(--border-color)] font-bold text-xs">
                                    Discard
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmShopPhoto}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 via-rose-500 to-violet-600 text-white font-bold text-xs shadow-md shadow-rose-500/20 hover:opacity-95 transition-opacity"
                                >
                                    <CheckCircle2 size={16} />
                                    Save Shop Visit
                                </button>
                            </div>
                        </section>
                    </div>
                )}

                {/* Edit Shop Details Modal */}
                {editingShop && (
                    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                        <section className="w-full max-w-lg bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl shadow-2xl p-5 space-y-4">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-widest text-[var(--accent-primary)]">Edit Shop #{editingShop.index + 1}</span>
                                    <h2 className="text-xl font-bold">Update Shop Details</h2>
                                    <p className="text-sm text-[var(--text-secondary)] mt-0.5">Modify location name or notes for this shop visit.</p>
                                </div>
                                <button type="button" onClick={() => setEditingShop(null)} className="w-9 h-9 rounded-xl border border-[var(--border-color)] flex items-center justify-center">
                                    <span className="text-lg leading-none">&times;</span>
                                </button>
                            </div>

                            {/* Preview */}
                            <img src={editingShop.data} alt="Shop preview" className="h-40 w-full rounded-2xl object-cover border border-[var(--border-color)]" />

                            {/* Captured time info */}
                            {(editingShop.uploadedAt || editingShop.capturedAt) && (
                                <div className="flex items-center gap-2 text-xs font-semibold text-[var(--text-secondary)] bg-[var(--bg-secondary)] px-3 py-2 rounded-xl border border-[var(--border-color)]">
                                    <Clock size={14} className="text-amber-500" />
                                    <span>
                                        Captured at: {new Date(editingShop.uploadedAt || editingShop.capturedAt).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                    </span>
                                </div>
                            )}

                            {/* Location name input */}
                            <div>
                                <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">
                                    Shop / Client Location Name
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. ABC Electronics, MG Road Branch"
                                    value={editingShop.locationName || ""}
                                    onChange={(e) => setEditingShop((prev) => ({ ...prev, locationName: e.target.value }))}
                                    className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3.5 py-2.5 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] transition-all"
                                />
                            </div>

                            {/* Description textarea */}
                            <div>
                                <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">
                                    Shop Visit Notes (No word limit)
                                </label>
                                <textarea
                                    rows={3}
                                    placeholder="Enter what was discussed, outcome, follow-up..."
                                    value={editingShop.description || ""}
                                    onChange={(e) => setEditingShop((prev) => ({ ...prev, description: e.target.value }))}
                                    className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3.5 py-2.5 text-sm font-medium outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] transition-all resize-none"
                                />
                            </div>

                            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end pt-2">
                                <button type="button" onClick={() => setEditingShop(null)} className="px-4 py-2 rounded-xl border border-[var(--border-color)] font-bold text-xs">
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={saveEditedShop}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[var(--accent-primary)] text-white font-bold text-xs shadow-md hover:opacity-95 transition-opacity"
                                >
                                    <CheckCircle2 size={16} />
                                    Update Details
                                </button>
                            </div>
                        </section>
                    </div>
                )}

                {salesVisitCameraOpen && (
                    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                        <section className="w-full max-w-lg bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl shadow-2xl p-5 space-y-4">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <span className="text-xs font-bold uppercase tracking-widest text-[var(--accent-primary)]">Shop visit proof</span>
                                    <h2 className="text-xl font-bold">Capture visit photo</h2>
                                </div>
                                <button
                                    type="button"
                                    onClick={stopSalesVisitCamera}
                                    className="w-9 h-9 rounded-xl border border-[var(--border-color)] flex items-center justify-center"
                                >
                                    <span className="text-lg leading-none">&times;</span>
                                </button>
                            </div>

                            <div className="overflow-hidden rounded-2xl border border-[var(--border-color)] bg-black">
                                <video ref={salesVisitVideoRef} autoPlay playsInline muted className="h-80 w-full object-cover" />
                            </div>

                            {salesVisitLocationError && (
                                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-700 dark:text-amber-300">
                                    {salesVisitLocationError} Keep the camera open, turn on GPS/location permission, then retry.
                                </div>
                            )}

                            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                                <button type="button" onClick={stopSalesVisitCamera} className="px-4 py-2 rounded-xl border border-[var(--border-color)] font-bold">
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={captureSalesVisitPhoto}
                                    disabled={salesVisitCameraLoading}
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[var(--accent-primary)] text-white font-bold disabled:opacity-60"
                                >
                                    <Camera size={16} />
                                    {salesVisitCameraLoading ? "Detecting location..." : salesVisitLocationError ? "Retry location" : "Capture Photo"}
                                </button>
                            </div>
                        </section>
                    </div>
                )}

                {earlyReasonOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
                        <form onSubmit={submitEarlyReason} className="w-full max-w-lg space-y-4 rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5 shadow-[var(--shadow-soft)]">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <span className="premium-section-kicker">Early EOD access</span>
                                    <h2 className="text-xl font-bold">Why are you closing the day early?</h2>
                                    <p className="mt-1 text-sm text-[var(--text-secondary)]">This reason is recorded for today's report.</p>
                                </div>
                                <button type="button" onClick={() => setEarlyReasonOpen(false)} className="h-9 w-9 rounded-xl border border-[var(--border-color)]">&times;</button>
                            </div>
                            <textarea value={earlyReason} onChange={(event) => setEarlyReason(event.target.value)} className="min-h-28 w-full resize-none rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-3 outline-none" placeholder="Explain the reason in at least 5 words" autoFocus required />
                            <div className="flex justify-end gap-2">
                                <button type="button" onClick={() => setEarlyReasonOpen(false)} className="rounded-xl border border-[var(--border-color)] px-4 py-2 font-bold">Cancel</button>
                                <button type="submit" disabled={earlyReasonSaving} className="premium-primary-button">{earlyReasonSaving ? "Submitting..." : "Submit & unlock"}</button>
                            </div>
                        </form>
                    </div>
                )}
                <ConfirmModal
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    onConfirm={submitEOD}
                    title="Submit EOD Report?"
                    message="Once submitted, this report will be marked complete for the selected date."
                    confirmText="Submit"
                    cancelText="Cancel"
                    loading={submitting}
                    variant="primary"
                />

                {submittedPopupOpen && (
                    <div className="eod-success-popup-backdrop" onMouseDown={() => setSubmittedPopupOpen(false)}>
                        <section className="eod-success-popup" onMouseDown={(event) => event.stopPropagation()}>
                            <div className="eod-success-icon">
                                <CheckCircle2 size={30} />
                            </div>
                            <h2>EOD Submitted</h2>
                            <p>Your report for {selectedDate} has been submitted successfully.</p>
                            <button type="button" onClick={() => setSubmittedPopupOpen(false)}>
                                Done
                            </button>
                        </section>
                    </div>
                )}
            </div>
    );
};

const ClassicReportField = ({ icon, label, helper, children }) => (
    <label className="eod-report-field">
        <span className="eod-field-label">
            <span>{icon}</span>
            <strong>{label}</strong>
        </span>
        <small>{helper}</small>
        {children}
    </label>
);

const EodMetric = ({ label, value }) => (
    <div className="eod-mini-metric">
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const ManufacturingRows = ({ title, actionLabel, rows, emptyRow, fields, onAdd, onChange, onRemove, onBlur, readOnly }) => (
    <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-sm font-black uppercase tracking-wide text-[var(--text-primary)]">{title}</h3>
            {!readOnly && (
                <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-3 py-2 text-xs font-bold"
                    onClick={() => onAdd(emptyRow)}
                >
                    <Plus size={14} />
                    {actionLabel}
                </button>
            )}
        </div>

        {rows.length === 0 ? (
            <p className="rounded-xl border border-dashed border-[var(--border-color)] px-3 py-4 text-sm text-[var(--text-secondary)]">No entries added yet.</p>
        ) : (
            <div className="space-y-3">
                {rows.map((row, index) => (
                    <div key={index} className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] p-3">
                        <div className="grid gap-3 md:grid-cols-2">
                            {fields.map((field) => (
                                <label key={field.key} className="text-xs font-bold text-[var(--text-secondary)]">
                                    {field.label}
                                    <input
                                        type={field.type || "text"}
                                        min={field.type === "number" ? "0" : undefined}
                                        value={row[field.key] || ""}
                                        onChange={(event) => onChange(index, field.key, event.target.value)}
                                        onBlur={onBlur}
                                        readOnly={readOnly}
                                        placeholder={field.placeholder || ""}
                                        className="mt-1 w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-sm font-semibold text-[var(--text-primary)] outline-none"
                                    />
                                </label>
                            ))}
                        </div>
                        {!readOnly && (
                            <button
                                type="button"
                                className="mt-3 inline-flex items-center gap-2 text-xs font-bold text-rose-500"
                                onClick={() => onRemove(index)}
                            >
                                <Trash2 size={13} />
                                Remove
                            </button>
                        )}
                    </div>
                ))}
            </div>
        )}
    </div>
);

const getMetricTitle = (team = "") => {
    const name = team.toLowerCase();
    if (name.includes("sales")) return "Sales snapshot";
    if (name.includes("tele")) return "Tele marketing snapshot";
    if (name.includes("digital")) return "Digital marketing snapshot";
    if (name.includes("manufactur")) return "Manufacturing production summary";
    return "Team snapshot";
};

const getTeamMetrics = (team = "") => {
    const name = team.toLowerCase();

    if (name.includes("sales")) {
        return [
            { name: "Deals Closed", type: "number" },
            { name: "Revenue", type: "text" },
            { name: "Client Meetings", type: "number" },
            { name: "Product Demos", type: "number" },
            { name: "Quotations Sent", type: "number" },
            { name: "Pipeline Added", type: "text" },
        ];
    }

    if (name.includes("tele")) {
        return [
            { name: "Total Calls", type: "number" },
            { name: "Connected Calls", type: "number" },
            { name: "Interested Leads", type: "number" },
            { name: "Follow-ups Scheduled", type: "number" },
            { name: "Demo Appointments", type: "number" },
            { name: "Converted Leads", type: "number" },
        ];
    }

    if (name.includes("digital")) {
        return [
            { name: "Active Campaigns", type: "text" },
            { name: "Leads Generated", type: "text" },
            { name: "Website Visits", type: "text" },
            { name: "Social Media Reach", type: "text" },
            { name: "Ad Spend Today", type: "text" },
            { name: "Conversion Rate", type: "text" },
        ];
    }

    if (name.includes("manufactur")) {
        return [
            { name: "Planned Production", type: "number" },
            { name: "Actual Production", type: "number" },
            { name: "Target Achieved %", type: "text" },
            { name: "Rejected Units", type: "number" },
            { name: "Rework Quantity", type: "number" },
            { name: "Downtime", type: "text" },
            { name: "Machine / Line", type: "text" },
            { name: "Pending Quantity", type: "number" },
            { name: "Material Shortage", type: "text" },
            { name: "Safety / 5S Status", type: "text" },
        ];
    }

    return [];
};

export default EODReport;
